import { useContext, useEffect, useState } from "react";
import { Typography, Icon, Box, Button, CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services/Config.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { Helmet } from "react-helmet";
import { customEvent } from "src/utils/gtag.js";
import {
  getCalendarApiUrl,
  getWebcalFormat,
  handleOpenWebcal,
} from "src/utils/Utils.js";
import { useSubscribe } from "src/utils/useSubscribe.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";

const VerifyScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { calendar = null } = location.state || {};
  const [isLoading, setIsLoading] = useState(false);
  const { subscribe } = useSubscribe();
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();

  const handleContinue = () => {
    // Track the continue button click
    customEvent({
      name: "user_click_continue_subscribing",
      category: "VerifyScreen",
      label: "Continue Subscribing",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });

    setIsLoading(true);

    try {
      // Subscribe to calendar in the background
      if (calendar) {
        subscribe({ calendar });
      }

      const calendarApiUrl = getCalendarApiUrl({
        calendar,
        user,
      });
      handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);

      // Wait a few seconds to show loading
      setTimeout(() => {
        setIsLoading(false);
        // Navigate to verify page after another delay
        setTimeout(() => {
          navigate(getPageRoute("help", "SUBSCRIBING_CALENDAR_VERIFY"), {
            state: { calendar, isReminder: false },
          });
        }, 2000);
      }, 2000);
    } catch (error) {
      setIsLoading(false);
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        error.message || "An error occurred. Please try again.",
        "error"
      );
    }
  };

  // Add page load tracking
  useEffect(() => {
    if (user) {
      customEvent({
        name: "landed_on_verify_screen",
        category: "VerifyScreen",
        label: "Landed on Verify Screen",
        data: {
          user_id: user._id,
          user_email: user.email,
          calendar_handle: calendar?.handle,
        },
      });
    }
  }, [Boolean(user), calendar?.handle]);

  useEffect(() => {
    // Scroll to top on load
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>Verify Subscription - Stanza</title>
        <meta
          name="description"
          content="Come back to verify to keep your subscription active"
        />
      </Helmet>

      <InstructionsLayout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            margin: "80px auto 0",
          }}
        >
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              display: "block",
              margin: "0",
              marginLeft: "0",
              marginRight: "auto",
              color: `${theme.palette.icon.primary}`,
              verticalAlign: "middle",
              fontSize: "2rem",
              mb: 1,
              fontWeight: 300,
              textAlign: "left",
              width: "100%",
            }}
          >
            keyboard_return
          </Icon>

          <Typography variant="h3" sx={{ mb: 1, textAlign: "left" }}>
            You'll need to <span style={{ fontWeight: "bold" }}>come back</span>{" "}
            to confirm your subscription.
          </Typography>

          <Button
            variant="contained"
            size="grown"
            onClick={handleContinue}
            disabled={isLoading}
            sx={{
              mt: 3.5,
              width: "100%",
              py: 1.5,
              borderRadius: 50,
              fontWeight: 500,
              textTransform: "none",
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              </>
            ) : (
              "GOT IT. CONTINUE SUBSCRIBING."
            )}
          </Button>
        </Box>
      </InstructionsLayout>
    </>
  );
};

export default VerifyScreen;
